import { useMemo } from 'react';
import type { ActionItem } from '../../redux/EditorSlice';
import { useAppSelector } from '../../redux/ReduxHooks';
import {
  collectDeclaredParentNames,
  deriveActionsFromCode,
  isAlwaysAvailableFrameworkActionItem,
} from '../../Components/CodeEditor/ConfigurationTabs/hardwareParser';

function actionKey(it: ActionItem): string {
  return [it.parentName || '', it.mechanismName || '', it.action?.name || ''].join('|');
}

/**
 * Returns an action list suitable for `ActionSelector` that includes:
 * - parsed actions from `state.editor.actionList` (most accurate when available)
 * - fallback-derived actions by scanning current program code for top-level mechanism assignments
 *
 * This lets panels that aren't mounted alongside Configure Hardware still show the full set of actions.
 * It also guards against stale parsed actions: if the Redux list's parent names don't appear in the
 * current code (e.g. switching projects before the parser re-runs), the stale list is discarded so
 * that previous-project actions never bleed into the new project's UI.
 */
export function useAllAvailableActions(): ActionItem[] {
  const parsedActions = useAppSelector((state) => state.editor.actionList) as ActionItem[];
  const code = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');

  return useMemo(() => {
    const parsed = parsedActions || [];

    if (!code) return parsed;

    const declaredParentNames = collectDeclaredParentNames(code);

    // Guard against stale parsed actions (project switching / between parse runs):
    // if we can observe any declared parents in current code, only keep parsed actions
    // that still correspond to a currently declared parent.
    const parsedToUse =
      declaredParentNames.size === 0
        ? parsed
        : parsed.filter((item) => {
            const parentName = String(item.parentName || '').trim();
            if (isAlwaysAvailableFrameworkActionItem(item)) return true;
            return parentName.length > 0 && declaredParentNames.has(parentName);
          });
    const derived = deriveActionsFromCode(code);

    const withParsedFirst = [...parsedToUse, ...derived];
    const deduped: ActionItem[] = [];
    const dedupSeen = new Set<string>();
    withParsedFirst.forEach((it) => {
      const k = actionKey(it);
      if (dedupSeen.has(k)) return;
      dedupSeen.add(k);
      deduped.push(it);
    });

    return deduped;
  }, [code, parsedActions]);
}
